import { Check, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

type DocumentReceivedStatus = 'received' | 'not_received' | null;

interface DocumentReceivedCardProps {
  title: string;
  badge: string;
  status: DocumentReceivedStatus;
  onReceivedClick: () => void;
  onNotReceivedClick: () => void;
  isLoading?: boolean;
}

/**
 * DocumentReceivedCard - Binary toggle card for marking intake documents as received/not received.
 *
 * Follows CASE_INGESTION.md compact density styling:
 * - Card content: p-2 (8px padding)
 * - Button height: h-7 (28px)
 * - Text size: text-xs (12px)
 * - Icon size: h-3 w-3 (12px)
 * - Gaps: gap-1.5 (6px) for buttons, gap-2 (8px) for content
 */
export const DocumentReceivedCard = ({
  title,
  badge,
  status,
  onReceivedClick,
  onNotReceivedClick,
  isLoading = false,
}: DocumentReceivedCardProps) => {
  return (
    <Card className="border-zinc-200">
      <CardContent className="p-2">
        <div className="flex items-center justify-between">
          {/* Left: Badge + Title */}
          <div className="flex items-center gap-2">
            <div className="flex h-6 w-6 items-center justify-center rounded bg-zinc-800 text-xs font-semibold text-white">
              {badge}
            </div>
            <span className="text-xs font-medium text-zinc-900">{title}</span>
          </div>

          {/* Right: Toggle Buttons */}
          <div className="flex gap-1.5">
            <Button
              type="button"
              variant={status === 'received' ? 'default' : 'outline'}
              size="sm"
              className="h-7 gap-1 text-xs"
              onClick={onReceivedClick}
              disabled={isLoading}
            >
              <Check className="h-3 w-3" />
              Received
            </Button>
            <Button
              type="button"
              variant={status === 'not_received' ? 'secondary' : 'outline'}
              size="sm"
              className="h-7 gap-1 text-xs"
              onClick={onNotReceivedClick}
              disabled={isLoading}
            >
              <X className="h-3 w-3" />
              Not Rcvd
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
